import { isEmpty, map } from 'underscore';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Collapse,
  TextTitle,
  ValidatedBootstrapPanel,
  ValidatedFixedPanel,
  ValidatedPanel,
  getPanelErrorProps,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, andComp, entityIs, propertyIs, anyComp } from '@bingads-webui-campaign-react/form-error';
import { useFormService } from '@bingads-webui-react/form';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import {
  ValidatedWebsiteTitle, ValidatedWebsite as WebsiteControl,
  ValidatedLanguageTarget, LanguageTargetValidatorFactory, campaignLanguageStrings,
  ValidatedLocationTargetSetting, getLocationTargetTitleDetails,
  ValidatedAutoGeneratedAssets, getAutoGeneratedAssetsDetails,
  ValidatedConversionGoals,
  ValidatedDisclaimer,
} from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { loadBundleCountries, ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import {
  getConversionGoalTitleDetails,
  getDisclaimerTitleDetails,
} from '@bingads-webui-campaign-react/wizard-shared-components';

import { getStyles } from './styles';
import { StepHeader } from '../../shared/step-header';
import DisclaimerImg from '../../shared//media/disclaimer.svg';
import LocationImg from '../../shared//media/location.svg';
import LanguageImg from '../../shared//media/language.svg';
import AutoAssetsImg from '../../shared//media/auto-assets.svg';
import ConversionGoalsImg from '../../shared/media/conversion-goals.svg';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const campaignNameProp = andComp(entityIsCampaign, propertyIs('Name'));
const campaignLanguagesProp = andComp(entityIsCampaign, propertyIs('Languages'));
const campaignConversionProp = andComp(entityIs(ERROR_ENTITY.CampaignConversionGoal), propertyIs('CampaignConversionGoalAssociations'));
const campaignLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);
const autoGenerateAssetsEnabledProp = andComp(entityIsCampaign, propertyIs('AutoGeneratedAssetsEnabled'));
const disclaimerProp = anyComp(
  entityIs(ERROR_ENTITY.CampaignDisclaimerAdExtension),
  propertyIs('DisclaimerAdsEnabled')
);

const ValidatedWebsite = withControlErrorHandling(withUnvalidatedControlErrors(WebsiteControl));
const ServerValidatedLanguageTarget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLanguageTarget, { filter: campaignLanguagesProp }));
const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLocationTargetSetting, { filter: campaignLocationCriterions }));
const ServerValidatedAutoGeneratedAssets = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedAutoGeneratedAssets, { filter: autoGenerateAssetsEnabledProp }));
const ServerValidatedDisclaimer = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedDisclaimer, { filter: disclaimerProp }));
const ServerValidatedConversionGoals = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedConversionGoals, { filter: campaignConversionProp }));

export const CampaignSettingsStep = ({
  data,
  onDataChange,
  error,
}) => {
  const {
    campaign,
    bundleCountries,
    bundleLocationCriterions,
    websites,
  } = data || {};

  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values;
  const classes = useDefaultStyles(getStyles);

  const serverContext = useContext(ServerContext);
  const {
    CurrentAccount: {AdvertiserCustomerID: advertiserCustomerId },
    CurrentLocale: locale,
    CurrentUser: { Market: market },
    DynamicPermissions,
    Permissions,
  } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const validateSpecialCharacters = permissions && permissions.dynamic && permissions.dynamic.PerformanceMaxCampaignsFinalURLValidation && permissions.dynamic.PMaxAndDSAFinalURLValidation;

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const { Languages } = campaign || {};
  const selectedLanguages = useRef(Languages);
  selectedLanguages.current = Languages;

  const refreshLocationTargetsTimes = useRef(0);

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    ['campaign', 'locations', 'languages', 'auto-generate-assets'] /* all sections */,
    ['campaign', 'locations', 'languages', 'auto-generate-assets'] /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignSettingsExpandedSections }),
    [setCampaignSettingsExpandedSections]
  );

  const onValidate = useCallback((result) => {
    if (data && data.campaign) {
      const newLanguages = result && result.data && result.data.Languages;
      if (!isEmpty(newLanguages) && selectedLanguages.current !== newLanguages) {
        selectedLanguages.current = newLanguages;
        refreshLocationTargetsTimes.current = (refreshLocationTargetsTimes.current + 1) % 1000;
        loadBundleCountries({
          cid, aid, serverContext, language: newLanguages,
        }).then((bundleCountriesAndLocationCriterions) => {
          onDataChange({
            data: {
              ...data,
              campaign: {
                ...data.campaign,
                ...result.data,
              },
              ...bundleCountriesAndLocationCriterions,
            },
            isValid: result.isValid,
          });
        });
      } else {
        onDataChange({
          data: {
            ...data,
            campaign: {
              ...data.campaign,
              ...result.data,
            },
          },
          isValid: result.isValid,
        });
      }
    }
  }, [aid, cid, data, onDataChange, serverContext]);

  const basicSettingsSection = useMemo(() => {
    const campaignPanel = (
      <ValidatedFixedPanel
        key="campaign"
        title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <RemainingCharsValidatedTextField
          placeholder={i18n.getString(_TL_('Enter a name for your campaign'))}
          id="input-campaign-name"
          validationProps={{
            field: 'Name',
            schema: Campaign,
          }}
          i18n={i18n}
          formErrorProps={{ filter: campaignNameProp }}
        />
      </ValidatedFixedPanel>
    );

    const locationsPanel = (
      <ValidatedPanel
        key="locations"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Location')}
          topicKey="pop_BA_CampaignCreate_Settings_LocationTargeting"
          details={
            getLocationTargetTitleDetails(
              i18n,
              campaign && campaign.locationTargets,
              bundleCountries,
              false,
              campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type,
              true
            )}
          icon={LocationImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'locations',
        }}
      >
        <ServerValidatedLocationTargetSetting
          validationProps={{
            data: campaign && campaign.locationTargets,
          }}
          bundleCountries={bundleCountries}
          bundleLocationCriterions={bundleLocationCriterions}
          refreshLocationTargetsTimes={refreshLocationTargetsTimes.current}
          language={Languages}
          market={market}
          permissions={permissions}
          scope={scope}
          bidStrategyType={campaign && campaign.DisplayBiddingStrategyType}
        />
      </ValidatedPanel>
    );

    const languagesPanel = (
      <ValidatedPanel
        key="languages"
        title={<TextTitle
          titleKey={_TL_('Languages')}
          topicKey="pop_BA_CampaignCreate_Settings_LanguageTargeting"
          details={campaign && map(campaign.Languages, lang => i18n.getString(campaignLanguageStrings[lang] || _TL_(`${lang}`))).join(', ')}
          icon={LanguageImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'languages',
        }}
      >
        <ServerValidatedLanguageTarget
          locale={locale}
          permissions={permissions}
          validationProps={{
            field: 'Languages',
            validator: LanguageTargetValidatorFactory(i18n),
          }}
        />
      </ValidatedPanel>
    );

    const basicSettings = [
      campaignPanel,
      locationsPanel,
      languagesPanel,
    ];

    return (
      <div className={classNames('basic-campaign-section')}>
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          { basicSettings }
        </Collapse>
      </div>
    );
  }, [Languages, allCampaignSettingsSections, basicSettingsExpand, bundleCountries, bundleLocationCriterions, campaign, campaignSettingsExpandedSections, i18n, locale, market, permissions, scope, setCampaignSettingsExpandedSections]);

  const autoGenerateSection = useMemo(() => {
    const autoGenerateAssetsPanel = (
      <ValidatedPanel
        key="auto-generate-assets"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Autogenerated assets')}
          icon={AutoAssetsImg}
          topicKey="pop_BA_RSA_AutoAssets"
          details={getAutoGeneratedAssetsDetails(i18n, campaign && campaign.AutoGeneratedAssetsEnabled)}
          isRecommended
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedAutoGeneratedAssets
          validationProps={{
            field: 'AutoGeneratedAssetsEnabled',
            schema: Campaign,
          }}
        />
      </ValidatedPanel>
    );

    const autoGenerateSettings = [
      autoGenerateAssetsPanel,
    ];

    return (
      <div className={classNames('auto-generate-section')}>
        <h3 className="section-title">{i18n.getString(_TL_('Automatically generate assets for your ads'))}</h3>
        <div className="section-description">
          {i18n.getString(_TL_('Maximize ad performance by increasing the amount of relevant assets available for your responsive search ads. We automatically generate assets, so you don’t have to create them manually.'))}
        </div>
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          { autoGenerateSettings }
        </Collapse>
      </div>
    );
  }, [allCampaignSettingsSections, campaign, campaignSettingsExpandedSections, i18n, setCampaignSettingsExpandedSections]);

  const [
    allAdvancedSettingsSections,
    advancedSettingsExpandedSections,
    setAdvancedSettingsExpandedSections,
  ] = useCollapseState(
    ['conversions', 'disclaimer'] /* all sections */,
    ['conversions', 'disclaimer'] /* initially expanded sections */
  );

  const allAdvancedSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdvancedSettingsExpandedSections }),
    [setAdvancedSettingsExpandedSections]
  );

  const [advancedSettingsSectionExpanded, setAdvancedSettingsSectionExpanded] = useState(false);
  const onAdvancedSettingsSectionToggle = useCallback((expanded) => {
    setAdvancedSettingsSectionExpanded(expanded);
  }, []);

  const advancedSettingsSection = useMemo(() => {
    const conversionsPanel = (
      <ValidatedPanel
        key="conversions"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Conversions')}
          details={getConversionGoalTitleDetails(i18n, campaign && campaign.conversionGoals)}
          icon={ConversionGoalsImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        topicKey="pop_BA_PMAX_Conversions"
      >
        <ServerValidatedConversionGoals
          validationProps={{
            initialValue: data && data.campaign,
            data: data && data.campaign,
            onValidate,
          }}
          cid={advertiserCustomerId}
          aid={aid}
          i18n={i18n}
        />
      </ValidatedPanel>
    );

    const disclaimerPanel = (
      <ValidatedPanel
        key="disclaimer"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Disclaimer')}
          details={getDisclaimerTitleDetails(i18n, campaign && campaign.disclaimer)}
          icon={DisclaimerImg}
          topicKey="pop_BA_DisclaimerAd"
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedDisclaimer
          campaignId={campaign && campaign.Id}
          validationProps={{
            data: {
              disclaimerAdsEnabled: campaign && campaign.DisclaimerAdsEnabled,
              disclaimer: campaign && campaign.disclaimer,
            },
          }}
        />
      </ValidatedPanel>
    );

    const advancedSettings = [
      conversionsPanel,
      disclaimerPanel,
    ];

    return (
      <div className={classNames('advanced-campaign-section', classes.advancedCampaignSection)}>
        <ValidatedBootstrapPanel
          onToggle={onAdvancedSettingsSectionToggle}
          expanded={advancedSettingsSectionExpanded}
          actuationProps={{
            expand: allAdvancedSettingsExpand,
          }}
          className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('Advanced settings'))}
                </BootstrapPanel.Title>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('advanced-settings-section')}>
                    <Collapse allPanelItems={allAdvancedSettingsSections} checkItems skipItemsTypeCheck activeKeys={advancedSettingsExpandedSections} setActiveKeys={setAdvancedSettingsExpandedSections}>
                      { advancedSettings }
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
            )}
        />
      </div>
    );
  }, [advancedSettingsExpandedSections, advancedSettingsSectionExpanded, aid, allAdvancedSettingsExpand, allAdvancedSettingsSections, campaign, cid, classes.advancedCampaignSection, classes.collapsiblePanel, data, i18n, onAdvancedSettingsSectionToggle, onValidate, setAdvancedSettingsExpandedSections]);

  const onValidateWebsite = useCallback((result) => {
    if (data) {
      onDataChange({
        data: {
          ...data,
          ...result.data,
        },
        isValid: result.isValid,
      });
    }
  }, [data, onDataChange]);

  const websiteSection = useMemo(() => (
    <Collapse allPanelItems={['website']} checkItems skipItemsTypeCheck>
      <ValidatedFixedPanel
        key="website"
        title={<ValidatedWebsiteTitle />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ValidatedWebsite
          validationProps={{
            data: websites || [],
            debounce: 700,
          }}
          validateOnRender
          isEmptyValid
          enableSpecialCharactersValidation={validateSpecialCharacters}
          useFluentV2
        />
      </ValidatedFixedPanel>
    </Collapse>
  ), [websites, validateSpecialCharacters]);

  return (
    <div className={classNames(classes.campaignSettingsStep, 'campaign-settings-step')}>
      <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <StepHeader
              title={_TL_('Campaign settings')}
              description={_TL_('Add your campaign details and choose where you want your ads to appear.')}
            />
            <div className="server-errors">
              <UnmappedFormErrors />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <ValidationGroup
              validationProps={{
                onValidate: onValidateWebsite,
                i18n: legacyI18n,
              }}
            >
              { websiteSection }
            </ValidationGroup>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate,
                data: campaign || {},
                i18n: legacyI18n,
              }}
            >
              <div className={classes.formContainer}>
                { basicSettingsSection }
                { autoGenerateSection }
                { advancedSettingsSection }
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

CampaignSettingsStep.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Id: PropTypes.string,
      Name: PropTypes.string,
      locationTargets: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    websites: PropTypes.shape([]),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

CampaignSettingsStep.defaultProps = {
  error: null,
};
